import React from "react";
import { Link } from "react-router-dom";
import useScrollReveal from "../hooks/useScrollReveal";
import Icon from "../components/Icon";

export default function QuranReadingPage() {
  useScrollReveal();
  return (
    <>
      <section className="relative overflow-hidden pt-16 pb-20 md:pt-20 md:pb-32 bg-surface-bright">
        <div className="absolute inset-0 geometric-pattern-emerald opacity-40"></div>
        <div className="absolute -top-24 -right-24 w-96 h-96 bg-primary-fixed opacity-20 blur-3xl rounded-full"></div>
        <div className="absolute -bottom-24 -left-24 w-96 h-96 bg-tertiary-fixed opacity-20 blur-3xl rounded-full"></div>
        <div className="relative page-x max-w-max-width mx-auto flex flex-col md:flex-row items-center gap-8 md:gap-16">
          <div className="flex-1 space-y-6">
            <div className="inline-flex items-center gap-2 px-4 py-1 rounded-full bg-primary-container text-on-primary-container font-label-md text-label-md">
              <Icon name="stars" className="material-symbols-outlined text-sm" />
              Fluency &amp; Confidence
            </div>
            <h1 className="text-display-responsive text-primary leading-tight">
              Quran Reading Program
            </h1>
            <p className="font-body-lg text-body-lg text-on-surface-variant max-w-xl">
              Transition smoothly from basic letters to fluent recitation. Our personalized curriculum focuses on developing steady rhythm, confident word connection, and a lifelong bond with the Holy Quran.
            </p>
            <div className="flex flex-col xs:flex-row flex-wrap gap-3 sm:gap-4 pt-4">
              <Link
                to="/contact"
                className="inline-block bg-primary text-on-primary px-6 sm:px-8 py-3 sm:py-4 rounded-full w-full sm:w-auto text-center font-headline-md text-headline-md shadow-lg shadow-primary/20 hover:scale-105 active:scale-95 transition-all"
              >
                Start Free Trial
              </Link>
            </div>
          </div>
          <div className="flex-1 relative">
            <div className="rounded-3xl overflow-hidden shadow-2xl border-4 md:border-8 border-white">
              <img
                alt="Parent and child learning Quran together"
                className="w-full h-auto object-cover"
                src="https://lh3.googleusercontent.com/aida-public/AB6AXuAvE1DWORR14rOC7SQaBGPiMbbm30B3zNXV5jZ5dnSuMwrMnIUJUq1dBlFU-vA4ixLpBxkZvrB1O0MUE6RgBZ2eqql6rdk9S_1cXSbayu9q4AVYVI5BL5l-rQ_VuFKbQz7BFu7XwPw1kdUbOJUvqgguH71A4pE-DmeTOuuNZ77XujQkH2x71mZLW1Qlu_kAo9WHLlvW_mq57KHObDJmbUOywsajoBr9kt9SlKulWMJPIipqZbN00k8Z9wD9MJpIvoe-RHDO3SIZybcD"
              />
            </div>
            <div className="relative md:absolute md:-bottom-8 md:-left-8 mt-6 md:mt-0 bg-white p-4 sm:p-6 rounded-2xl shadow-xl border border-outline-variant flex items-center gap-4 max-w-[calc(100vw-2rem)] md:max-w-none">
              <div className="bg-tertiary-container p-3 rounded-full">
                <Icon
                  name="menu_book"
                  className="material-symbols-outlined text-on-tertiary-container"
                  filled
                />
              </div>
              <div>
                <p className="font-label-md text-label-md text-on-surface-variant">
                  Recitation Focus
                </p>
                <p className="font-headline-md text-headline-md text-primary">
                  100% Fluency
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="section-y bg-white">
        <div className="page-x max-w-max-width mx-auto">
          <div className="text-center mb-10 md:mb-16 space-y-4">
            <h2 className="text-headline-responsive text-primary">
              Expected Outcomes
            </h2>
            <p className="font-body-md text-body-md text-on-surface-variant max-w-2xl mx-auto">
              Our structured approach guarantees clear progress in your journey to reading the Quran smoothly and beautifully.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-gutter">
            <div className="group card-pad rounded-2xl border border-outline-variant hover:border-primary hover:shadow-xl transition-all duration-300 scroll-reveal">
              <div className="w-16 h-16 bg-surface-container rounded-full flex items-center justify-center mb-6 group-hover:bg-primary-container transition-colors">
                <Icon
                  name="speed"
                  className="material-symbols-outlined text-primary text-3xl group-hover:text-on-primary-container"
                />
              </div>
              <h3 className="font-headline-md text-headline-md text-on-surface mb-3">
                Fluent Recitation
              </h3>
              <p className="font-body-md text-body-md text-on-surface-variant">
                Master the flow and connection of Quranic words to recite verses smoothly without hesitation.
              </p>
            </div>

            <div className="group card-pad rounded-2xl border border-outline-variant hover:border-primary hover:shadow-xl transition-all duration-300 scroll-reveal">
              <div className="w-16 h-16 bg-surface-container rounded-full flex items-center justify-center mb-6 group-hover:bg-primary-container transition-colors">
                <Icon
                  name="visibility"
                  className="material-symbols-outlined text-primary text-3xl group-hover:text-on-primary-container"
                />
              </div>
              <h3 className="font-headline-md text-headline-md text-on-surface mb-3">
                Word Recognition
              </h3>
              <p className="font-body-md text-body-md text-on-surface-variant">
                Instantly identify complex diacritics, signs, and vowel marks to maintain accurate reading.
              </p>
            </div>

            <div className="group card-pad rounded-2xl border border-outline-variant hover:border-primary hover:shadow-xl transition-all duration-300 scroll-reveal">
              <div className="w-16 h-16 bg-surface-container rounded-full flex items-center justify-center mb-6 group-hover:bg-primary-container transition-colors">
                <Icon
                  name="favorite"
                  className="material-symbols-outlined text-primary text-3xl group-hover:text-on-primary-container"
                />
              </div>
              <h3 className="font-headline-md text-headline-md text-on-surface mb-3">
                Daily Connection
              </h3>
              <p className="font-body-md text-body-md text-on-surface-variant">
                Develop a habit and love for daily Quranic recitation, building spiritual consistency.
              </p>
            </div>

            <div className="group card-pad rounded-2xl border border-outline-variant hover:border-primary hover:shadow-xl transition-all duration-300 scroll-reveal">
              <div className="w-16 h-16 bg-surface-container rounded-full flex items-center justify-center mb-6 group-hover:bg-primary-container transition-colors">
                <Icon
                  name="auto_stories"
                  className="material-symbols-outlined text-primary text-3xl group-hover:text-on-primary-container"
                />
              </div>
              <h3 className="font-headline-md text-headline-md text-on-surface mb-3">
                Read Independently
              </h3>
              <p className="font-body-md text-body-md text-on-surface-variant">
                Gain the skill to open any page of the Quran and read confidently without constant supervision.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="section-y bg-surface-container-low">
        <div className="page-x max-w-max-width mx-auto flex flex-col md:flex-row items-center gap-8 md:gap-16">
          <div className="flex-1 space-y-8">
            <h2 className="text-headline-responsive text-primary">
              Who is this program for?
            </h2>
            <div className="space-y-6">
              <div className="flex gap-4">
                <div className="mt-1 flex-shrink-0 w-10 h-10 rounded-full bg-tertiary-fixed flex items-center justify-center">
                  <Icon
                    name="done"
                    className="material-symbols-outlined text-on-tertiary-fixed"
                  />
                </div>
                <div>
                  <h4 className="font-headline-md text-headline-md text-on-surface">
                    Qaida Graduates
                  </h4>
                  <p className="font-body-md text-body-md text-on-surface-variant">
                    Students who have completed Noorani Qaida (or equivalent basic rules) and are ready to read directly from the Mushaf.
                  </p>
                </div>
              </div>
              <div className="flex gap-4">
                <div className="mt-1 flex-shrink-0 w-10 h-10 rounded-full bg-primary-fixed flex items-center justify-center">
                  <Icon
                    name="done"
                    className="material-symbols-outlined text-on-primary-fixed"
                  />
                </div>
                <div>
                  <h4 className="font-headline-md text-headline-md text-on-surface">
                    Those Seeking Fluency
                  </h4>
                  <p className="font-body-md text-body-md text-on-surface-variant">
                    Individuals who know basic pronunciation but struggle with reading speed, pausing, and smooth word connection.
                  </p>
                </div>
              </div>
            </div>
          </div>
          <div className="flex-1 bg-white p-8 rounded-3xl border border-outline-variant shadow-lg space-y-6">
            <h3 className="font-headline-md text-primary text-center">Program Structure</h3>
            <ul className="space-y-4">
              <li className="flex items-start gap-3">
                <Icon name="check_circle" className="text-primary mt-1" />
                <div>
                  <span className="font-bold text-on-surface">Step 1: Diagnostic Assessment</span>
                  <p className="font-body-md text-on-surface-variant">We assess your current speed and accuracy to set the ideal pace.</p>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <Icon name="check_circle" className="text-primary mt-1" />
                <div>
                  <span className="font-bold text-on-surface">Step 2: Structured Reading Sessions</span>
                  <p className="font-body-md text-on-surface-variant">Guided reading of short surahs, progressing to longer verses systematically.</p>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <Icon name="check_circle" className="text-primary mt-1" />
                <div>
                  <span className="font-bold text-on-surface">Step 3: Confidence Building</span>
                  <p className="font-body-md text-on-surface-variant">Exercises designed to eliminate pauses and build automatic recognition.</p>
                </div>
              </li>
            </ul>
          </div>
        </div>
      </section>

      <section className="section-y page-x max-w-max-width mx-auto">
        <div className="gradient-mesh rounded-section p-8 md:p-16 flex flex-col md:flex-row items-center justify-between border border-primary-fixed/30 gap-8">
          <div className="max-w-xl">
            <h2 className="text-display-responsive text-primary mb-4 font-bold">
              Ready to start reading the Quran fluently?
            </h2>
            <p className="text-body-lg text-on-surface-variant">
              Take the first step with our certified Quran tutors and build a deep, meaningful connection to the Holy Quran.
            </p>
          </div>
          <div className="flex flex-col sm:flex-row gap-4 w-full md:w-auto">
            <Link
              to="/contact"
              className="bg-primary text-on-primary px-8 py-4 rounded-full text-center font-bold shadow-lg hover:shadow-xl hover:scale-105 active:scale-95 transition-all"
            >
              Book Free Trial
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}
